import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowRight, ChevronLeft, ChevronRight, Clock, Award, MapPin } from "lucide-react";

const sections = [
  {
    id: "about",
    title: "About Appface",
    content: (
      <>
        <p className="text-lg md:text-xl text-[#86868B] leading-relaxed mb-8">
          Appface Technologies Pvt Ltd delivers engineering and support services across Linux infrastructure, 
          HPC operations, AI enablement, and modern application development. We focus on outcomes: stability, 
          performance, security, and speed of delivery.
        </p>
        <div className="flex items-center gap-3 text-[#86868B]">
          <MapPin size={20} className="text-[#2997FF]" />
          <span>Headquarters: Bengaluru, India</span>
        </div>
      </>
    )
  },
  {
    id: "stats",
    title: "By the Numbers",
    content: (
      <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-8">
        {[
          { value: "10+", label: "Years delivering", icon: Clock },
          { value: "24/7", label: "Support models", icon: Award },
          { value: "100%", label: "Client retention", icon: CheckCircle }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="text-center">
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 mx-auto mb-4 sm:mb-5 md:mb-8 lg:mb-10 rounded-lg sm:rounded-xl md:rounded-2xl bg-[#2997FF]/10 flex items-center justify-center">
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-[#2997FF]" />
              </div>
              <div className="stat-number text-base sm:text-lg md:text-4xl mb-1 md:mb-2">{stat.value}</div>
              <p className="text-[#86868B] text-[10px] sm:text-xs md:text-sm leading-tight">{stat.label}</p>
            </div>
          );
        })}
      </div>
    )
  },
  {
    id: "facts",
    title: "Quick Facts",
    content: (
      <div className="flex justify-center">
        <div className="flex flex-col gap-4 text-left">
          {[
            "Engineering-first, documentation-heavy",
            "Security and reliability baked in",
            "Clean handover: runbooks + SOPs",
            "Flexible engagement models"
          ].map((fact, index) => (
            <div key={index} className="flex items-center gap-3 text-lg text-[#86868B]">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2997FF] flex-shrink-0" />
              <span>{fact}</span>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: "values",
    title: "Our Values",
    content: (
      <div className="grid grid-cols-2 gap-4">
        {[
          { title: "Engineering Excellence", desc: "Robust, maintainable solutions that stand the test of time." },
          { title: "Transparent Communication", desc: "Clear documentation and regular updates keep everyone aligned." },
          { title: "Security First", desc: "Security and reliability are foundational, not afterthoughts." },
          { title: "Results-Driven", desc: "We measure success by the outcomes we deliver." }
        ].map((value, index) => (
          <div key={index} className="service-card p-5">
            <h4 className="text-base font-semibold text-[#F5F5F7] mb-2">{value.title}</h4>
            <p className="text-sm text-[#86868B]">{value.desc}</p>
          </div>
        ))}
      </div>
    )
  }
];

const AboutPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? sections.length - 1 : prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === sections.length - 1 ? 0 : prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-black min-h-[100dvh] flex items-center">
        <div className="max-w-[1200px] mx-auto px-6 w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center gap-8">
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="about-prev"
              aria-label="Previous section"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F5F5F7] tracking-tight mb-10" data-testid="about-title">
                    {sections[currentIndex].title}
                  </h1>
                  <div className="max-w-3xl mx-auto">
                    {sections[currentIndex].content}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex items-center justify-center gap-2 mt-12">
                {sections.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-6" : "bg-[#86868B]/50 hover:bg-[#86868B]"
                    }`}
                    aria-label={`Go to section ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="about-next"
              aria-label="Next section"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center min-h-[300px]"
              >
                <h1 className="text-2xl font-semibold text-[#F5F5F7] tracking-tight mb-8">
                  {sections[currentIndex].title}
                </h1>
                <div className="max-w-full">
                  {sections[currentIndex].content}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-6 mt-10">
              <button
                onClick={goToPrevious}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Previous section"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {sections.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-4" : "bg-[#86868B]/50"
                    }`}
                    aria-label={`Go to section ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Next section"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1C1C1E]">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold text-[#F5F5F7] tracking-tight mb-4">
              Ready to work together?
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto mb-8">
              Let's discuss how we can help your organization achieve its goals.
            </p>
            <Link
              to="/contact"
              className="btn-primary px-8 py-4 rounded-full text-base font-medium inline-flex items-center gap-2"
              data-testid="about-cta"
            >
              Contact Us
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
